// 日志终端三 ring buffer / Three-tab ring buffer with dedupe + subscribe.
// 每个 tab 有独立上限；rawKey 已存在的 entry 丢弃；subscribe 在 append/clear 时回调。
// 完全不碰 DOM——UI 层通过 subscribe 订阅后自行渲染。

import type { TerminalEntry, TerminalTab } from "@/features/log-terminal/terminal-types";

const SESSION_LOG_STORAGE_KEY = "ripple:log-terminal:session-info:v1";
const TERMINAL_TABS = new Set<TerminalTab>(["system", "planA", "planB"]);

// 上限（系统 tab 更小：它只承载偶发事件） / per-tab caps.
export const terminalBufferLimits = {
  system: 500,
  plan: 2000,
};

interface TabStore {
  entries: TerminalEntry[];
  seen: Set<string>;
  listeners: Set<() => void>;
}

function newStore(): TabStore {
  return { entries: [], seen: new Set(), listeners: new Set() };
}

const stores: Record<TerminalTab, TabStore> = {
  system: newStore(),
  planA: newStore(),
  planB: newStore(),
};

function limitFor(tab: TerminalTab): number {
  return tab === "system" ? terminalBufferLimits.system : terminalBufferLimits.plan;
}

function notify(tab: TerminalTab): void {
  for (const fn of stores[tab].listeners) fn();
}

function sortEntries(store: TabStore): void {
  store.entries.sort((a, b) => a.timestamp - b.timestamp);
}

function trimToLimit(tab: TerminalTab): void {
  const store = stores[tab];
  const max = limitFor(tab);
  if (store.entries.length <= max) return;
  const dropped = store.entries.splice(0, store.entries.length - max);
  for (const d of dropped) store.seen.delete(d.rawKey);
}

function persistSessionEntries(): void {
  const storage = storageArea();
  if (!storage) return;
  const entries = (["system", "planA", "planB"] as TerminalTab[])
    .flatMap((tab) => stores[tab].entries)
    .filter((entry) => entry.severity === "info");
  try {
    storage.setItem(SESSION_LOG_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // 浏览器可能禁用 sessionStorage；日志显示不应因此中断。
    // sessionStorage can be disabled; logging must continue without persistence.
  }
}

function restoreSessionEntries(): void {
  const storage = storageArea();
  if (!storage) return;
  let parsed: unknown;
  try {
    const raw = storage.getItem(SESSION_LOG_STORAGE_KEY);
    if (!raw) return;
    parsed = JSON.parse(raw);
  } catch {
    return;
  }
  if (!Array.isArray(parsed)) return;
  for (const entry of parsed) {
    if (!isPersistedSessionEntry(entry)) continue;
    const store = stores[entry.tab];
    if (store.seen.has(entry.rawKey)) continue;
    store.seen.add(entry.rawKey);
    store.entries.push(entry);
  }
  for (const tab of ["system", "planA", "planB"] as TerminalTab[]) {
    sortEntries(stores[tab]);
    trimToLimit(tab);
  }
}

function isPersistedSessionEntry(value: unknown): value is TerminalEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<TerminalEntry>;
  return (
    typeof entry.tab === "string" &&
    TERMINAL_TABS.has(entry.tab as TerminalTab) &&
    typeof entry.timestamp === "number" &&
    Number.isFinite(entry.timestamp) &&
    entry.severity === "info" &&
    typeof entry.text === "string" &&
    (entry.density === "compact" || entry.density === "verbose") &&
    typeof entry.rawKey === "string" &&
    (entry.phase === undefined || typeof entry.phase === "string")
  );
}

function storageArea(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

restoreSessionEntries();

/**
 * 追加一条日志 / Append one entry.
 * 若 rawKey 已存在直接丢弃，否则入栈、按时间排序并在溢出时裁掉最旧。
 * Drop duplicate rawKey; otherwise insert, sort by timestamp, and trim oldest entries.
 */
export function appendEntry(entry: TerminalEntry): void {
  const store = stores[entry.tab];
  if (store.seen.has(entry.rawKey)) return;
  store.seen.add(entry.rawKey);
  store.entries.push(entry);
  sortEntries(store);
  trimToLimit(entry.tab);
  persistSessionEntries();
  notify(entry.tab);
}

/**
 * 批量回放 / Batch replay (one notify per tab touched).
 */
export function replayEntries(entries: TerminalEntry[]): void {
  const touched = new Set<TerminalTab>();
  for (const entry of entries) {
    const store = stores[entry.tab];
    if (store.seen.has(entry.rawKey)) continue;
    store.seen.add(entry.rawKey);
    store.entries.push(entry);
    touched.add(entry.tab);
  }
  for (const tab of touched) {
    sortEntries(stores[tab]);
    trimToLimit(tab);
    notify(tab);
  }
  persistSessionEntries();
}

/** 读取某 tab 所有条目（按时间顺序） / Read all entries for a tab. */
export function getEntries(tab: TerminalTab): ReadonlyArray<TerminalEntry> {
  return stores[tab].entries;
}

/** 清空某 tab / Clear a tab; fires notify. */
export function clearTab(tab: TerminalTab): void {
  stores[tab].entries = [];
  stores[tab].seen = new Set();
  persistSessionEntries();
  notify(tab);
}

/** 订阅某 tab 的变动；返回 unsubscribe / Subscribe to buffer changes. */
export function subscribe(tab: TerminalTab, fn: () => void): () => void {
  stores[tab].listeners.add(fn);
  return () => {
    stores[tab].listeners.delete(fn);
  };
}

/** 只供测试：重置全部 buffer / Test-only: reset everything. */
export function resetBuffer(): void {
  for (const tab of ["system", "planA", "planB"] as TerminalTab[]) {
    stores[tab] = newStore();
  }
  storageArea()?.removeItem(SESSION_LOG_STORAGE_KEY);
}
